import React, { useState } from "react";
import { Box, Stack, Typography, Container, Modal, IconButton } from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";
import { Eye, X, ChevronDown } from "lucide-react";

// Import Personal template images (latest set)
import personalImage0 from "../assets/templates/personal/Personal + Add ons(2)-pages-images-0.jpg";
import personalImage1 from "../assets/templates/personal/Personal + Add ons(2)-pages-images-1.jpg";
import personalImage2 from "../assets/templates/personal/Personal + Add ons(2)-pages-images-2.jpg";
import personalImage3 from "../assets/templates/personal/Personal + Add ons(2)-pages-images-3.jpg";
import personalImage4 from "../assets/templates/personal/Personal + Add ons(2)-pages-images-4.jpg";
import personalImage5 from "../assets/templates/personal/Personal + Add ons(2)-pages-images-5.jpg";

// Import Total template images
import totalImage0 from "../assets/templates/total/Total + Add ons-pages-images-0.jpg";
import totalImage1 from "../assets/templates/total/Total + Add ons-pages-images-1.jpg";
import totalImage2 from "../assets/templates/total/Total + Add ons-pages-images-2.jpg";
import totalImage3 from "../assets/templates/total/Total + Add ons-pages-images-3.jpg";
import totalImage4 from "../assets/templates/total/Total + Add ons-pages-images-4.jpg";

// Import Compact template images
import compactImage0 from "../assets/templates/compact/Compact + Add ons(3)-pages-images-0.jpg";
import compactImage1 from "../assets/templates/compact/Compact + Add ons(3)-pages-images-1.jpg";
import compactImage2 from "../assets/templates/compact/Compact + Add ons(3)-pages-images-2.jpg";
import compactImage3 from "../assets/templates/compact/Compact + Add ons(3)-pages-images-3.jpg";
import compactImage4 from "../assets/templates/compact/Compact + Add ons(3)-pages-images-4.jpg";

// Image arrays for each template
// Personal: use all latest images, show 2nd image as card preview
const personalImages = [personalImage0, personalImage1, personalImage2, personalImage3, personalImage4, personalImage5];
const totalImages = [totalImage0, totalImage1, totalImage2, totalImage3, totalImage4];
const compactImages = [compactImage0, compactImage1, compactImage2, compactImage3, compactImage4];

const TemplatesContainer = styled(Box)(({ theme }) => ({
  width: "95%",
  margin: "auto",
  backgroundColor: "transparent",
  padding: "60px 0 80px 0",
  position: "relative",
  overflow: "hidden",
  paddingTop: "70px",
  [theme.breakpoints.down("md")]: {
    padding: "40px 0 60px 0",
    paddingTop: "50px",
  },
}));

// Ultra-Premium Template Card
const TemplateCard = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  flex: "1 1 0",
  background: "linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 255, 0.98) 50%, rgba(232, 244, 255, 0.95) 100%)",
  backdropFilter: "blur(20px)",
  borderRadius: "32px 32px 20px 20px",
  padding: "36px",
  paddingBottom: "24px",
  border: "1px solid rgba(70, 116, 217, 0.15)",
  transition: "all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "6px",
    background: "linear-gradient(90deg, #4674D9 0%, #6E91EB 30%, #8FB3FF 60%, #B8D4FF 100%)",
    opacity: 0,
    transition: "opacity 0.5s ease",
    borderRadius: "32px 32px 0 0",
    boxShadow: "0 2px 8px rgba(70, 116, 217, 0.3)",
  },
  "&:hover": {
    transform: "translateY(-12px) scale(1.02)",
    borderColor: "rgba(70, 116, 217, 0.3)",
    background: "linear-gradient(145deg, rgba(255, 255, 255, 1) 0%, rgba(240, 246, 255, 1) 50%, rgba(227, 242, 255, 1) 100%)",
    "&::before": {
      opacity: 1,
    },
  },
  [theme.breakpoints.down("md")]: {
    width: "100%",
    marginBottom: theme.spacing(3),
    padding: "24px",
    paddingBottom: "20px",
    flex: "none",
  },
}));

// Premium Image Container - shows full image
const ImageContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  borderRadius: "20px",
  overflow: "hidden",
  marginBottom: theme.spacing(2.5),
  position: "relative",
  background: "#FFFFFF",
  border: "3px solid #E8EDF5",
  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
  "&:hover": {
    "& img": {
      transform: "scale(1.02)",
    },
  },
  [theme.breakpoints.down("md")]: {
    marginBottom: theme.spacing(2),
    borderRadius: "16px",
  },
}));

// Preview Button - standalone at bottom
const PreviewButton = styled(Box)(({ theme }) => ({
  background: "linear-gradient(135deg, #4674D9 0%, #6E91EB 100%)",
  color: "#FFFFFF",
  borderRadius: "50px",
  padding: "12px 24px",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "10px",
  fontSize: "15px",
  fontWeight: 600,
  fontFamily: "Inter, sans-serif",
  boxShadow: "0 4px 16px rgba(70, 116, 217, 0.3)",
  marginTop: "auto",
  "&:hover": {
    transform: "translateY(-3px)",
    boxShadow: "0 8px 24px rgba(70, 116, 217, 0.4)",
    background: "linear-gradient(135deg, #3563C8 0%, #5D80DA 100%)",
  },
  [theme.breakpoints.down("md")]: {
    padding: "10px 20px",
    fontSize: "14px",
    gap: "8px",
  },
}));

// Common styles for card title
const cardTitleStyles = {
  color: "#1D47A4",
  fontFamily: "Inter, sans-serif",
  fontSize: { xs: "22px", md: "26px" },
  fontWeight: 700,
  lineHeight: { xs: "28px", md: "32px" },
  letterSpacing: "-0.02em",
  mb: 1,
};

// Common styles for card description
const cardDescriptionStyles = {
  color: "#4A5568",
  fontFamily: "Inter, sans-serif",
  fontSize: { xs: "14px", md: "15px" },
  fontWeight: 400,
  lineHeight: { xs: "22px", md: "24px" },
  mb: 2.5,
  flexGrow: 1,
};

// Common styles for card images
const cardImageStyles = {
  width: "100%",
  height: "auto",
  display: "block",
  transition: "transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
};

const TemplatesSection = () => {
  const [openModal, setOpenModal] = useState(false);
  const [currentImages, setCurrentImages] = useState([]);
  const [currentTitle, setCurrentTitle] = useState("");

  const handleOpenModal = (images, title) => {
    setCurrentImages(images);
    setCurrentTitle(title);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setCurrentImages([]);
    setCurrentTitle("");
  };

  return (
    <TemplatesContainer>
      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
        {/* Heading Section with Animation */}
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          sx={{
            textAlign: "center",
            mb: { xs: 3, md: 4 },
          }}
        >
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="baseline"
            sx={{ mb: 2 }}
          >
            <Typography
              sx={{
                color: "#282D4D",
                fontFamily: "Inter, sans-serif",
                fontSize: { xs: "28px", sm: "36px", md: "48.5px" },
                fontWeight: 700,
                mr: 1,
                lineHeight: { xs: "36px", md: "1.2" },
              }}
            >
              Report
            </Typography>
            <Typography
              sx={{
                color: "#4674D9",
                fontFamily: "Inter, sans-serif",
                fontSize: { xs: "28px", sm: "36px", md: "46.9px" },
                fontWeight: 700,
                lineHeight: { xs: "36px", md: "1.2" },
              }}
            >
              Templates
            </Typography>
          </Stack>

          <Typography
            sx={{
              color: "#2E2E2F",
              fontFamily: "Inter, sans-serif",
              fontSize: { xs: "14px", md: "18px" },
              fontWeight: 400,
              textAlign: "center",
              maxWidth: "800px",
              margin: "0 auto",
              lineHeight: { xs: "22px", md: "28px" },
              px: { xs: 1, md: 0 },
            }}
          >
            A complete reporting ecosystem designed for modern healthcare
          </Typography>
        </Box>

        {/* Template Cards - Three Individual Cards */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "center",
            alignItems: { xs: "stretch", md: "stretch" },
            gap: { xs: 3, md: 4 },
            mt: { xs: 4, md: 6 },
            maxWidth: "1300px",
            margin: { xs: "24px auto 0", md: "48px auto 0" },
            px: { xs: 2, md: 0 },
          }}
        >
          {/* Card 1: Personal Report */}
          <TemplateCard
            component={motion.div}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            {/* Template Image - Using 2nd image (index 1) as preview */}
            <ImageContainer>
              <Box
                component="img"
                src={personalImages[1]}
                alt="Personal Report"
                sx={cardImageStyles}
              />
            </ImageContainer>

            {/* Title */}
            <Typography sx={cardTitleStyles}>
              Personal Report
            </Typography>

            {/* Description */}
            <Typography sx={cardDescriptionStyles}>
              Patient-friendly layout highlighting critical parameters with visual alerts
            </Typography>

            {/* Preview Button - At the end */}
            <PreviewButton onClick={() => handleOpenModal(personalImages, "Personal Report")}>
              <Eye size={18} />
              Preview
            </PreviewButton>
          </TemplateCard>

          {/* Card 2: Total Report */}
          <TemplateCard
            component={motion.div}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.45, duration: 0.6 }}
          >
            {/* Template Image - Using 2nd image (index 1) */}
            <ImageContainer>
              <Box
                component="img"
                src={totalImages[1]}
                alt="Total Report"
                sx={cardImageStyles}
              />
            </ImageContainer>

            {/* Title */}
            <Typography sx={cardTitleStyles}>
              Total Report
            </Typography>

            {/* Description */}
            <Typography sx={cardDescriptionStyles}>
              Deep-dive interpretation with complete biomarker insights & explanation notes
            </Typography>

            {/* Preview Button - At the end */}
            <PreviewButton onClick={() => handleOpenModal(totalImages, "Total Report")}>
              <Eye size={18} />
              Preview
            </PreviewButton>
          </TemplateCard>

          {/* Card 3: Compact Report */}
          <TemplateCard
            component={motion.div}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6, duration: 0.6 }}
          >
            {/* Template Image - Using 2nd image (index 1) */}
            <ImageContainer>
              <Box
                component="img"
                src={compactImages[1]}
                alt="Compact Report"
                sx={cardImageStyles}
              />
            </ImageContainer>

            {/* Title */}
            <Typography sx={cardTitleStyles}>
              Compact Report
            </Typography>

            {/* Description */}
            <Typography sx={cardDescriptionStyles}>
              Minimal pages for quick review & teleconsultations
            </Typography>

            {/* Preview Button - At the end */}
            <PreviewButton onClick={() => handleOpenModal(compactImages, "Compact Report")}>
              <Eye size={18} />
              Preview
            </PreviewButton>
          </TemplateCard>
        </Box>
      </Container>

      {/* Gallery Modal for scrollable image preview - FIXED POSITIONING */}
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="gallery-preview-modal"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backdropFilter: "blur(12px)",
          backgroundColor: "rgba(0, 0, 0, 0.7)",
        }}
      >
        <Box
          component={motion.div}
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          sx={{
            width: { xs: "95%", sm: "90%", md: "80%", lg: "70%" },
            maxWidth: "700px",
            maxHeight: { xs: "85vh", sm: "88vh", md: "90vh" },
            background: "#FFFFFF",
            borderRadius: { xs: "20px", md: "24px" },
            outline: "none",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 25px 80px rgba(0, 0, 0, 0.35)",
            overflow: "hidden",
            position: "relative",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: { xs: "16px 20px", md: "20px 28px" },
              borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
              background: "linear-gradient(180deg, #FFFFFF 0%, #F8FAFF 100%)",
              flexShrink: 0,
            }}
          >
            <Box>
              <Typography
                id="gallery-preview-modal"
                sx={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: { xs: "20px", sm: "24px" },
                  fontWeight: 700,
                  color: "#1D47A4",
                  letterSpacing: "-0.02em",
                }}
              >
                {currentTitle}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: { xs: "12px", md: "13px" },
                  fontWeight: 500,
                  color: "#6B7280",
                  mt: 0.5,
                }}
              >
                {currentImages.length} {currentImages.length === 1 ? "page" : "pages"}
              </Typography>
            </Box>
            <IconButton
              onClick={handleCloseModal}
              sx={{
                color: "#4674D9",
                backgroundColor: "rgba(70, 116, 217, 0.08)",
                width: { xs: "40px", md: "44px" },
                height: { xs: "40px", md: "44px" },
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: "rgba(70, 116, 217, 0.15)",
                  transform: "rotate(90deg)",
                },
              }}
            >
              <X size={22} />
            </IconButton>
          </Box>

          {/* Scrollable Gallery Body */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              overflowX: "hidden",
              padding: { xs: "16px", md: "24px" },
              background: "#F5F7FA",
              scrollBehavior: "smooth",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#E8EDF5",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "linear-gradient(180deg, #4674D9 0%, #6E91EB 100%)",
                borderRadius: "4px",
                "&:hover": {
                  background: "linear-gradient(180deg, #3563C8 0%, #5D80DA 100%)",
                },
              },
            }}
          >
            {currentImages.map((image, index) => (
              <Box
                key={index}
                component={motion.div}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.4 }}
                sx={{
                  background: "#FFFFFF",
                  borderRadius: { xs: "12px", md: "16px" },
                  padding: { xs: "12px", md: "16px" },
                  marginBottom: index < currentImages.length - 1 ? { xs: "16px", md: "20px" } : 0,
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 30px rgba(70, 116, 217, 0.15)",
                  },
                }}
              >
                {/* Page Label */}
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    background: "linear-gradient(135deg, #4674D9 0%, #6E91EB 100%)",
                    color: "#FFFFFF",
                    padding: { xs: "5px 12px", md: "6px 14px" },
                    borderRadius: "20px",
                    fontSize: { xs: "12px", md: "13px" },
                    fontWeight: 600,
                    fontFamily: "Inter, sans-serif",
                    marginBottom: { xs: "10px", md: "12px" },
                  }}
                >
                  Page {index + 1} of {currentImages.length}
                </Box>
                <Box
                  component="img"
                  src={image}
                  alt={`${currentTitle} - Page ${index + 1}`}
                  sx={{
                    width: "100%",
                    height: "auto",
                    borderRadius: { xs: "8px", md: "12px" },
                    display: "block",
                    boxShadow: "0 2px 12px rgba(0, 0, 0, 0.06)",
                  }}
                />
              </Box>
            ))}
          </Box>

          {/* Scroll Indicator - only show if more than 1 page */}
          {currentImages.length > 1 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
                padding: { xs: "10px 16px", md: "12px 20px" },
                background: "linear-gradient(180deg, #F8FAFF 0%, #FFFFFF 100%)",
                borderTop: "1px solid rgba(0, 0, 0, 0.06)",
                color: "#6B7280",
                fontSize: { xs: "12px", md: "13px" },
                fontFamily: "Inter, sans-serif",
                fontWeight: 500,
                flexShrink: 0,
                "& svg": {
                  animation: "bounce 1.5s infinite",
                },
                "@keyframes bounce": {
                  "0%, 100%": {
                    transform: "translateY(0)",
                  },
                  "50%": {
                    transform: "translateY(4px)",
                  },
                },
              }}
            >
              <ChevronDown size={18} />
              Scroll to view all pages
            </Box>
          )}
        </Box>
      </Modal>
    </TemplatesContainer>
  );
};

export default TemplatesSection;
